/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */
/** @typedef {import('./floorPlanDesignerState.js').Point} Point */

import {
  addWall,
  cleanupFloorPlanState,
  getPointById,
  getWallSegmentsResolved,
  splitWallAtPoint,
} from './floorPlanDesignerState.js';

const CROSSING_EPS = 1e-4;
const ENDPOINT_T_EPS = 0.01;

/**
 * @param {Point} a
 * @param {Point} b
 * @param {Point} c
 * @param {Point} d
 * @returns {number}
 */
function cross2d(a, b, c, d) {
  return (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
}

/**
 * @param {Point} a
 * @param {Point} b
 * @param {Point} c
 * @param {Point} d
 * @returns {boolean}
 */
export function segmentsIntersectProper(a, b, c, d) {
  const d1 = cross2d(a, b, a, c);
  const d2 = cross2d(a, b, a, d);
  const d3 = cross2d(c, d, c, a);
  const d4 = cross2d(c, d, c, b);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

/**
 * @param {Point} a
 * @param {Point} b
 * @param {Point} c
 * @param {Point} d
 * @returns {Point | null}
 */
export function segmentIntersectionPoint(a, b, c, d) {
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  const sx = d[0] - c[0];
  const sy = d[1] - c[1];
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < CROSSING_EPS) return null;
  const qx = c[0] - a[0];
  const qy = c[1] - a[1];
  const t = (qx * sy - qy * sx) / denom;
  const u = (qx * ry - qy * rx) / denom;
  if (t <= ENDPOINT_T_EPS || t >= 1 - ENDPOINT_T_EPS || u <= ENDPOINT_T_EPS || u >= 1 - ENDPOINT_T_EPS) {
    return null;
  }
  return [a[0] + t * rx, a[1] + t * ry];
}

/**
 * @param {DesignerState} state
 * @param {string} wallIdA
 * @param {string} wallIdB
 * @returns {Point | null}
 */
export function findWallCrossingPoint(state, wallIdA, wallIdB) {
  if (wallIdA === wallIdB) return null;
  const wallA = state.walls.find((w) => w.id === wallIdA);
  const wallB = state.walls.find((w) => w.id === wallIdB);
  if (!wallA || !wallB) return null;

  const a0 = getPointById(state, wallA.startPointId);
  const a1 = getPointById(state, wallA.endPointId);
  const b0 = getPointById(state, wallB.startPointId);
  const b1 = getPointById(state, wallB.endPointId);
  if (!a0 || !a1 || !b0 || !b1) return null;

  const shared =
    wallA.startPointId === wallB.startPointId ||
    wallA.startPointId === wallB.endPointId ||
    wallA.endPointId === wallB.startPointId ||
    wallA.endPointId === wallB.endPointId;
  if (shared) return null;

  const A = [a0.x, a0.y];
  const B = [a1.x, a1.y];
  const C = [b0.x, b0.y];
  const D = [b1.x, b1.y];

  if (!segmentsIntersectProper(A, B, C, D)) return null;
  return segmentIntersectionPoint(A, B, C, D);
}

/**
 * @typedef {{ wallId: string; point: Point; tOnNewWall: number }} CrossingHit
 */

/**
 * @param {DesignerState} state
 * @param {string} startPointId
 * @param {string} endPointId
 * @param {string} [excludeWallId]
 * @returns {CrossingHit[]}
 */
export function findCrossingsForSegment(state, startPointId, endPointId, excludeWallId = '') {
  const startPt = getPointById(state, startPointId);
  const endPt = getPointById(state, endPointId);
  if (!startPt || !endPt) return [];

  const A = [startPt.x, startPt.y];
  const B = [endPt.x, endPt.y];
  const dx = B[0] - A[0];
  const dy = B[1] - A[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) return [];

  /** @type {CrossingHit[]} */
  const hits = [];

  for (const wall of state.walls) {
    if (wall.id === excludeWallId) continue;
    const b0 = getPointById(state, wall.startPointId);
    const b1 = getPointById(state, wall.endPointId);
    if (!b0 || !b1) continue;
    const C = [b0.x, b0.y];
    const D = [b1.x, b1.y];
    if (
      wall.startPointId === startPointId ||
      wall.startPointId === endPointId ||
      wall.endPointId === startPointId ||
      wall.endPointId === endPointId
    ) {
      continue;
    }
    if (!segmentsIntersectProper(A, B, C, D)) continue;
    const pt = segmentIntersectionPoint(A, B, C, D);
    if (!pt) continue;
    const t = ((pt[0] - A[0]) * dx + (pt[1] - A[1]) * dy) / lenSq;
    hits.push({ wallId: wall.id, point: pt, tOnNewWall: t });
  }

  hits.sort((a, b) => a.tOnNewWall - b.tOnNewWall);
  return hits;
}

/**
 * Add a wall segment, splitting existing walls and chaining through intersection nodes.
 * @param {DesignerState} state
 * @param {string} startPointId
 * @param {string} endPointId
 * @returns {DesignerState}
 */
export function addWallResolvingCrossings(state, startPointId, endPointId) {
  if (startPointId === endPointId) return state;

  const hits = findCrossingsForSegment(state, startPointId, endPointId);
  if (hits.length === 0) {
    return addWall(state, startPointId, endPointId);
  }

  const hit = hits[0];
  const splitExisting = splitWallAtPoint(state, hit.wallId, hit.point, { skipCleanup: true });
  let next = splitExisting.state;
  const pointId = splitExisting.pointId;
  if (!pointId) {
    return addWall(state, startPointId, endPointId);
  }

  next = addWallResolvingCrossings(next, startPointId, pointId);
  next = addWallResolvingCrossings(next, pointId, endPointId);
  return cleanupFloorPlanState(next);
}

/**
 * @param {DesignerState} state
 * @returns {DesignerState}
 */
export function resolveAllWallCrossings(state) {
  let next = state;
  const maxIterations = 64;

  for (let iter = 0; iter < maxIterations; iter += 1) {
    const segments = getWallSegmentsResolved(next);
    let found = false;

    for (let i = 0; i < segments.length && !found; i += 1) {
      for (let j = i + 1; j < segments.length; j += 1) {
        const wallA = next.walls.find((w) => w.id === segments[i].id);
        const wallB = next.walls.find((w) => w.id === segments[j].id);
        if (!wallA || !wallB) continue;
        const pt = findWallCrossingPoint(next, wallA.id, wallB.id);
        if (!pt) continue;

        const splitA = splitWallAtPoint(next, wallA.id, pt);
        next = splitA.state;
        const splitB = splitWallAtPoint(next, wallB.id, pt);
        next = splitB.state;
        found = true;
        break;
      }
    }

    if (!found) break;
  }

  return next;
}
